import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Card, CardBody, CardHeader, Col, Container, Row, Nav, NavItem, NavLink,
  TabContent, TabPane, Button, Input, Label, Spinner, Badge, Collapse,
  Progress, Modal, ModalHeader, ModalBody, FormFeedback, Form, Alert,
} from "reactstrap";
import classnames from "classnames";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { api } from "../../config";
import { getAPIKey, createNewAPIKey, requestFaucetCode } from "../../helpers/fakebackend_helper";
import "./ApiPlayground.css";

const BASE_URL = api.API_URL;
const POLL_INTERVAL_MS = 4000;
const POLL_MAX_ATTEMPTS = 150; // ~10 minutes
const TERMINAL_STATUSES = ["COMPLETED", "FAILED", "CANCELLED"];

// Endpoint IDs that have a downloadable Postman collection at /postman/<id>.postman_collection.json
const POSTMAN_AVAILABLE = new Set([
  "image_to_image", "image_translate", "object_detect", "enhance_image", "remove_background", "remove_object",
  "swap_outfit", "image_to_video", "transition_video", "lipsync",
  "video_audio_image", "video_audio_reference", "video_audio_transition", "video_audio_mv", "video_audio_text",
  "swap_face_image", "swap_face_video", "liveportrait", "audio_to_video", "ai_avatar",
  "text_to_image", "check_nsfw", "call_vllm", "identify_stone", "identify_antique",
  "identify_coin_ai", "suggest", "with_prompt", "with_lyrics", "audio_seperate",
]);

// ─── API Endpoint Definitions ───
const IMAGE_ENDPOINTS = [
  {
    id: "text_to_image", name: "Text to Image", method: "POST",
    path: "/api-call-v4/generate/text_to_image", bodyType: "json",
    description: "Generate an image from a text prompt",
    icon: "ri-quill-pen-line", color: "#7c3aed",
    fields: [
      { key: "prompt", label: "Prompt", type: "textarea", required: true, placeholder: "Describe the image you want to generate..." },
      { key: "width", label: "Width", type: "number", default: 768 },
      { key: "height", label: "Height", type: "number", default: 1024 },
    ],
  },
  {
    id: "image_to_image", name: "Image to Image", method: "POST",
    path: "/api-call-v4/generate/image_to_image", bodyType: "formdata",
    description: "Transform 1–3 images with a prompt",
    icon: "ri-image-edit-line", color: "#2563eb",
    fields: [
      { key: "image_url", label: "Image 1", type: "file", required: true },
      { key: "image2_url", label: "Image 2 (optional)", type: "file" },
      { key: "image3_url", label: "Image 3 (optional)", type: "file" },
      { key: "prompt", label: "Prompt", type: "textarea", required: true, placeholder: "Describe how to transform the image..." },
      { key: "width", label: "Width (optional)", type: "number", placeholder: "Leave empty to keep source ratio" },
      { key: "height", label: "Height (optional)", type: "number", placeholder: "Leave empty to keep source ratio" },
    ],
  },
  {
    id: "image_translate", name: "Image Translate", method: "POST",
    path: "/api-call-v4/generate/image_translate", bodyType: "formdata",
    description: "Detect text in an image (OCR), translate it, and re-render it back onto the image",
    icon: "ri-translate-2", color: "#0ea5e9",
    fields: [
      { key: "image_url", label: "Image", type: "file", required: true },
      { key: "target_lang", label: "Target Language", type: "text", required: true, default: "vi", placeholder: "e.g. vi, en, ja" },
      { key: "source_lang", label: "Source Language (optional)", type: "text", default: "auto", placeholder: "auto" },
    ],
  },
  {
    id: "object_detect", name: "Object Detect", method: "POST",
    path: "/api-call-v4/generate/object_detect", bodyType: "formdata",
    description: "Detect objects in an image (YOLO), draw boxes/labels, and return the annotated image URL + detections",
    icon: "ri-focus-3-line", color: "#16a34a",
    fields: [
      { key: "image_url", label: "Image", type: "file", required: true },
      { key: "labels_lang", label: "Label Language", type: "text", default: "en", placeholder: "e.g. en, vi, ja" },
      { key: "conf", label: "Confidence (0-1, optional)", type: "number", placeholder: "0.35" },
      { key: "show_conf", label: "Show confidence %", type: "select", options: ["true", "false"], default: "true" },
    ],
  },
  {
    id: "remove_background", name: "Remove Background", method: "POST",
    path: "/api-call-v4/generate/remove_background", bodyType: "formdata",
    description: "Remove the background from an image",
    icon: "ri-eraser-line", color: "#059669",
    fields: [
      { key: "image_url", label: "Image", type: "file", required: true },
    ],
  },
  {
    id: "enhance_image", name: "Enhance Image", method: "POST",
    path: "/api-call-v4/generate/enhance_image", bodyType: "formdata",
    description: "Upscale and enhance image quality",
    icon: "ri-sparkling-line", color: "#d97706",
    fields: [
      { key: "image_url", label: "Image", type: "file", required: true },
    ],
  },
  {
    id: "remove_object", name: "Remove Object", method: "POST",
    path: "/api-call-v4/generate/remove_object", bodyType: "formdata",
    description: "Remove unwanted objects from an image",
    icon: "ri-scissors-cut-line", color: "#dc2626",
    fields: [
      { key: "image_url", label: "Image", type: "file", required: true },
    ],
  },
  {
    id: "swap_face_image", name: "Swap Face Image", method: "POST",
    path: "/api-call-v4/generate/swap_face_image", bodyType: "formdata",
    description: "Swap a face from a source image onto a target image",
    icon: "ri-user-shared-line", color: "#db2777",
    fields: [
      { key: "source_url", label: "Source Image (face to use)", type: "file", required: true },
      { key: "target_url", label: "Target Image (where to put the face)", type: "file", required: true },
      { key: "face_pair", label: "Face Pair (optional, JSON)", type: "textarea", placeholder: '[{"source_index": 0, "target_index": 0}]', isJson: true },
    ],
  },
  {
    id: "check_nsfw", name: "Check NSFW", method: "POST",
    path: "/api-call-v4/generate/check_nsfw", bodyType: "json",
    description: "Check if an image contains NSFW content",
    icon: "ri-shield-check-line", color: "#4f46e5",
    fields: [
      { key: "image_url", label: "Image URL", type: "text", required: true, placeholder: "https://example.com/image.jpg" },
    ],
  },
  {
    id: "call_vllm", name: "Call VLLM", method: "POST",
    path: "/api-call-v4/generate/call_vllm", bodyType: "json",
    description: "Call Vision Language Model for image analysis",
    icon: "ri-robot-line", color: "#be185d",
    fields: [
      { key: "messages", label: "Messages (JSON)", type: "textarea", required: true, placeholder: '[\n  {"role": "user", "content": "Describe this image"}\n]', isJson: true },
    ],
  },
];

const VIDEO_ENDPOINTS = [
  {
    id: "lipsync", name: "Lipsync Video", method: "POST",
    path: "/api-call-v4/generate/lipsync", bodyType: "formdata",
    description: "Animate a portrait to lipsync with an audio clip",
    icon: "ri-mic-2-line", color: "#0d9488",
    fields: [
      { key: "image_url", label: "Image", type: "file", required: true },
      { key: "audio_url", label: "Audio", type: "file", accept: "audio/*", required: true },
      { key: "duration", label: "Duration (seconds)", type: "number", default: 5 },
      { key: "prompt", label: "Prompt (optional)", type: "textarea", placeholder: "Describe motion or expression..." },
      { key: "generate_image_prompt", label: "Generate Image Prompt (optional)", type: "textarea", placeholder: "If provided, an image-to-image step will run first using this prompt before lipsync..." },
    ],
  },
  {
    id: "image_to_video", name: "Image to Video", method: "POST",
    path: "/api-call-v4/generate/image_to_video", bodyType: "formdata",
    description: "Animate a static image into a video",
    icon: "ri-movie-line", color: "#7c3aed",
    fields: [
      { key: "image_url", label: "Image", type: "file", required: true },
      { key: "prompt", label: "Prompt", type: "textarea", placeholder: "Describe the motion..." },
      { key: "full_hd", label: "Full HD", type: "select", options: ["false", "true"], default: "false" },
      { key: "is_long_time", label: "Long Duration", type: "select", options: ["false", "true"], default: "false" },
    ],
  },
  {
    id: "swap_face_video", name: "Swap Face Video", method: "POST",
    path: "/api-call-v4/generate/swap_face_video", bodyType: "formdata",
    description: "Swap a face from a source image onto every frame of a target video",
    icon: "ri-user-voice-line", color: "#be185d",
    fields: [
      { key: "source_url", label: "Source Image (face to use)", type: "file", required: true },
      { key: "video_url", label: "Target Video", type: "file", accept: "video/*", required: true },
      { key: "face_pair", label: "Face Pair (optional, JSON)", type: "textarea", placeholder: '[{"source_index": 0, "target_index": 0}]', isJson: true },
    ],
  },
  {
    id: "transition_video", name: "Transition Video", method: "POST",
    path: "/api-call-v4/generate/transition_video", bodyType: "formdata",
    description: "Create a transition video between two images",
    icon: "ri-film-line", color: "#0891b2",
    fields: [
      { key: "image_url", label: "Image 1", type: "file", required: true },
      { key: "image2_url", label: "Image 2", type: "file", required: true },
      { key: "prompt", label: "Prompt", type: "textarea", placeholder: "Describe the transition..." },
      { key: "full_hd", label: "Full HD", type: "select", options: ["false", "true"], default: "false" },
      { key: "is_long_time", label: "Long Duration", type: "select", options: ["false", "true"], default: "false" },
    ],
  },
];

const VIDEO_AUDIO_ENDPOINTS = [
  {
    id: "video_audio_text", name: "Video + Audio (from Text)", method: "POST",
    path: "/api-call-v4/generate/video_audio_text", bodyType: "json",
    description: "Generate a video with synchronized sound from a text prompt alone — no input image needed",
    icon: "ri-quill-pen-line", color: "#16a34a",
    fields: [
      { key: "prompt", label: "Prompt", type: "textarea", required: true, placeholder: "Describe the scene, motion and the sounds you want to hear..." },
      { key: "duration", label: "Duration (seconds, 1-15)", type: "number", default: 5 },
      { key: "resolution", label: "Resolution (720p costs 2x credits)", type: "select", options: ["480p", "720p"], default: "480p" },
      { key: "aspect_ratio", label: "Aspect Ratio", type: "select", options: ["16:9", "9:16", "1:1"], default: "16:9" },
    ],
  },
  {
    id: "video_audio_image", name: "Video + Audio (from Image)", method: "POST",
    path: "/api-call-v4/generate/video_audio_image", bodyType: "formdata",
    description: "Animate an image into a video with synchronized generated sound — the image is used as the first frame",
    icon: "ri-film-line", color: "#7c3aed",
    fields: [
      { key: "image_url", label: "Image (first frame)", type: "file", required: true },
      { key: "prompt", label: "Prompt", type: "textarea", required: true, placeholder: "Describe the scene, motion and the sounds you want to hear..." },
      { key: "duration", label: "Duration (seconds, 1-15)", type: "number", default: 5 },
      { key: "resolution", label: "Resolution (720p costs 2x credits)", type: "select", options: ["480p", "720p"], default: "480p" },
    ],
  },
  {
    id: "video_audio_reference", name: "Video + Audio (from Reference)", method: "POST",
    path: "/api-call-v4/generate/video_audio_reference", bodyType: "formdata",
    description: "Generate a video with synchronized sound using 1–3 images as subject/style references — the scene is composed freely from your prompt",
    icon: "ri-clapperboard-line", color: "#0891b2",
    fields: [
      { key: "image_url", label: "Reference Image 1", type: "file", required: true },
      { key: "image2_url", label: "Reference Image 2 (optional)", type: "file" },
      { key: "image3_url", label: "Reference Image 3 (optional)", type: "file" },
      { key: "prompt", label: "Prompt", type: "textarea", required: true, placeholder: "Describe the subjects, scene, shots and soundscape..." },
      { key: "duration", label: "Duration (seconds, 1-15)", type: "number", default: 5 },
      { key: "resolution", label: "Resolution (720p costs 2x credits)", type: "select", options: ["480p", "720p"], default: "480p" },
    ],
  },
  {
    id: "video_audio_transition", name: "Video + Audio (Transition)", method: "POST",
    path: "/api-call-v4/generate/video_audio_transition", bodyType: "formdata",
    description: "Create a start→end frame transition video with synchronized generated sound",
    icon: "ri-swap-box-line", color: "#db2777",
    fields: [
      { key: "image_url", label: "Start Frame", type: "file", required: true },
      { key: "image2_url", label: "End Frame", type: "file", required: true },
      { key: "prompt", label: "Prompt", type: "textarea", required: true, placeholder: "Describe the transition, motion and the sounds you want to hear..." },
      { key: "duration", label: "Duration (seconds, 1-15)", type: "number", default: 5 },
      { key: "resolution", label: "Resolution (720p costs 2x credits)", type: "select", options: ["480p", "720p"], default: "480p" },
    ],
  },
  {
    id: "video_audio_mv", name: "MV Creator", method: "POST",
    path: "/api-call-v4/generate/video_audio_mv", bodyType: "formdata",
    description: "Turn a photo + a song into a cinematic music video — the performer keeps their identity and lip-syncs to your audio track",
    icon: "ri-music-2-fill", color: "#f59e0b",
    fields: [
      { key: "image_url", label: "Performer Image", type: "file", required: true },
      { key: "audio_url", label: "Audio Track", type: "file", accept: "audio/*", required: true },
      { key: "prompt", label: "Prompt", type: "textarea", required: true, placeholder: "Describe scenes, locations and transitions for the music video..." },
      { key: "duration", label: "Duration (seconds, 1-20)", type: "number", default: 20 },
      { key: "resolution", label: "Resolution (720p costs 2x credits)", type: "select", options: ["480p", "720p"], default: "480p" },
    ],
  },
];

const SONG_ENDPOINTS = [
  {
    id: "suggest", name: "Suggest Song", method: "POST",
    path: "/api-song-v2/generate/suggest", bodyType: "json",
    description: "Get song suggestions by genre and mood",
    icon: "ri-lightbulb-line", color: "#f59e0b",
    fields: [
      { key: "genre", label: "Genre", type: "text", default: "random", placeholder: "Pop, Rock, Jazz..." },
      { key: "mood", label: "Mood", type: "text", default: "random", placeholder: "Happy, Sad, Energetic..." },
      { key: "is_lyrics", label: "Include Lyrics", type: "select", options: ["false", "true"], default: "false" },
    ],
  },
  {
    id: "with_prompt", name: "Generate with Prompt", method: "POST",
    path: "/api-song-v2/generate/with-prompt", bodyType: "json",
    description: "Generate a song from a text description",
    icon: "ri-music-2-line", color: "#8b5cf6",
    fields: [
      { key: "prompt", label: "Prompt", type: "textarea", required: true, placeholder: "Describe the song you want..." },
      { key: "genre", label: "Genre", type: "text", placeholder: "pop, rock, jazz..." },
      { key: "vocal_gender", label: "Vocal Gender", type: "select", options: ["female", "male"], default: "female" },
      { key: "mood", label: "Mood", type: "text", placeholder: "happy, sad, energetic..." },
      { key: "webhook_url", label: "Webhook URL", type: "text", placeholder: "https://your-server.com/webhook" },
    ],
  },
  {
    id: "with_lyrics", name: "Generate with Lyrics", method: "POST",
    path: "/api-song-v2/generate/with-lyrics", bodyType: "json",
    description: "Generate a song from custom lyrics",
    icon: "ri-file-music-line", color: "#ec4899",
    fields: [
      { key: "lyrics", label: "Lyrics", type: "textarea", required: true, placeholder: "[Verse 1]\nYour lyrics here...\n\n[Chorus]\nChorus lyrics..." },
      { key: "genre", label: "Genre", type: "text", placeholder: "pop, rock, jazz..." },
      { key: "vocal_gender", label: "Vocal Gender", type: "select", options: ["female", "male"], default: "female" },
      { key: "mood", label: "Mood", type: "text", placeholder: "happy, sad, energetic..." },
      { key: "webhook_url", label: "Webhook URL", type: "text", placeholder: "https://your-server.com/webhook" },
    ],
  },
  {
    id: "audio_seperate", name: "Separate Audio", method: "POST",
    path: "/api-ai-cover/generate/seperate", bodyType: "formdata",
    description: "Split a song into vocal and instrumental stems",
    icon: "ri-scissors-2-line", color: "#10b981",
    fields: [
      { key: "audio_url", label: "Audio File", type: "file", accept: "audio/*", required: true },
      { key: "webhook_url", label: "Webhook URL (optional)", type: "text", placeholder: "https://your-server.com/webhook" },
    ],
  },
];

const IDENTIFY_ENDPOINTS = [
  {
    id: "identify_stone", name: "Identify Stone", method: "POST",
    path: "/api-call-v4/generate/identify_stone", bodyType: "formdata",
    description: "Identify a stone or gemstone from an image",
    icon: "ri-copper-diamond-line", color: "#a855f7",
    fields: [
      { key: "image_url", label: "Stone Image", type: "file", required: true },
      { key: "multiple", label: "Return multiple candidates", type: "select", options: ["false", "true"], default: "false" },
      { key: "max_results", label: "Max candidates (2–5, only when multiple=true)", type: "number", placeholder: "3" },
      { key: "language", label: "Output Language (optional)", type: "text", placeholder: "e.g. Vietnamese, English, Spanish" },
    ],
  },
  {
    id: "identify_antique", name: "Identify Antique", method: "POST",
    path: "/api-call-v4/generate/identify_antique", bodyType: "formdata",
    description: "Identify an antique or collectible from an image (returns structured JSON with price/origin/period estimates)",
    icon: "ri-treasure-map-line", color: "#b45309",
    fields: [
      { key: "image_url", label: "Antique Image", type: "file", required: true },
      { key: "language", label: "Output Language (optional)", type: "text", placeholder: "e.g. Vietnamese, English, Spanish" },
    ],
  },
  {
    id: "identify_coin_ai", name: "Identify Coin (AI)", method: "POST",
    path: "/api-call-v4/generate/identify_coin_ai", bodyType: "formdata",
    description: "Identify a coin from its front (and optional back) image — returns structured JSON with denomination, year, composition, grade and value estimates",
    icon: "ri-copper-coin-line", color: "#ca8a04",
    fields: [
      { key: "coin_front", label: "Coin Front (obverse)", type: "file", required: true },
      { key: "coin_back", label: "Coin Back (reverse, optional)", type: "file" },
      { key: "language", label: "Output Language (optional)", type: "text", placeholder: "e.g. Vietnamese, English, Spanish" },
    ],
  },
];

const REQUEST_STATUS_ENDPOINT = {
  id: "request_status", name: "Request Status", method: "GET",
  path: "/api-call/request-status", bodyType: "none",
  description: "Check the status of a previous request",
  icon: "ri-loader-line", color: "#10b981",
  fields: [
    { key: "request_id", label: "Request ID", type: "text", required: true, placeholder: "Enter request ID (UUID)" },
  ],
};

// ─── EndpointCard Component ───
const EndpointCard = ({ endpoint, apiKey, onResult }) => {
  const [formData, setFormData] = useState(() => {
    const init = {};
    endpoint.fields.forEach(f => { init[f.key] = f.default || ""; });
    return init;
  });
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (key, file) => {
    setFiles(prev => ({ ...prev, [key]: file }));
  };

  const uploadFileToR2 = async (file) => {
    const presignRes = await fetch(`${BASE_URL}/upload/presign`, {
      method: "POST",
      headers: { apikey: apiKey, "Content-Type": "application/json" },
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        size: file.size,
      }),
    });
    const presignJson = await presignRes.json();
    if (!presignJson || !presignJson.success || !presignJson.data) {
      throw new Error(`Presign failed: ${presignJson && presignJson.message ? presignJson.message : "unknown error"}`);
    }
    const { upload_url, public_url } = presignJson.data;

    const putRes = await fetch(upload_url, {
      method: "PUT",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!putRes.ok) {
      throw new Error(`R2 upload failed (${putRes.status}): ${await putRes.text().catch(() => "")}`);
    }
    return public_url;
  };

  const handleSubmit = async () => {
    if (!apiKey) {
      onResult({ error: true, message: "Please enter your API Key first" });
      return;
    }
    setLoading(true);
    setUploadStatus("");
    const startTime = Date.now();
    try {
      let url = BASE_URL + endpoint.path;
      let options = { method: endpoint.method, headers: { apikey: apiKey } };
      const fileFields = endpoint.fields.filter(f => f.type === "file" && files[f.key]);

      if (endpoint.id === "request_status") {
        url = url + "/" + formData.request_id;
      } else if (endpoint.bodyType === "json" || fileFields.length > 0 || endpoint.bodyType === "formdata") {
        // Always send as JSON; upload files via presigned R2 first.
        options.headers["Content-Type"] = "application/json";
        const body = {};

        // 1. Upload each file to R2, populate body[fileKey] = public URL
        for (let i = 0; i < fileFields.length; i++) {
          const f = fileFields[i];
          setUploadStatus(`Uploading ${f.label || f.key} (${i + 1}/${fileFields.length})...`);
          body[f.key] = await uploadFileToR2(files[f.key]);
        }
        setUploadStatus("");

        // 2. Add non-file fields
        endpoint.fields.forEach(f => {
          if (f.type === "file") return;
          if (formData[f.key] === undefined || formData[f.key] === "") return;
          if (f.isJson) {
            try { body[f.key] = JSON.parse(formData[f.key]); } catch { body[f.key] = formData[f.key]; }
          } else if (f.type === "number") {
            body[f.key] = Number(formData[f.key]);
          } else if (f.type === "select" && (formData[f.key] === "true" || formData[f.key] === "false")) {
            body[f.key] = formData[f.key] === "true";
          } else {
            body[f.key] = formData[f.key];
          }
        });
        options.body = JSON.stringify(body);
      }

      const res = await fetch(url, options);
      const data = await res.json();
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);
      onResult({ error: !res.ok, status: res.status, data, time: elapsed, endpoint: endpoint.name });
      // Notify header widget to refresh credit balance — request likely deducted credit.
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("credit-changed"));
      }
    } catch (err) {
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);
      onResult({ error: true, message: err.message, time: elapsed, endpoint: endpoint.name });
    } finally {
      setUploadStatus("");
      setLoading(false);
    }
  };

  return (
    <Card className="endpoint-card mb-3">
      <CardHeader className="endpoint-card-header" onClick={() => setIsOpen(!isOpen)} style={{ cursor: "pointer" }}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <div className="endpoint-icon-wrap">
              <i className={endpoint.icon}></i>
            </div>
            <div>
              <h6 className="mb-0 fw-semibold">{endpoint.name}</h6>
              <small className="text-muted">{endpoint.description}</small>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Badge color={endpoint.method === "GET" ? "success" : "primary"} className="method-badge">{endpoint.method}</Badge>
            <i className={`ri-arrow-${isOpen ? "up" : "down"}-s-line fs-18`}></i>
          </div>
        </div>
      </CardHeader>
      <Collapse isOpen={isOpen}>
        <CardBody className="endpoint-card-body">
          <div className="endpoint-path mb-3">
            <code>
              {BASE_URL}{endpoint.path}
              {endpoint.id === "request_status" && (
                <span style={{ color: "var(--ah-accent)" }}>
                  /{formData.request_id ? formData.request_id : "{request_id}"}
                </span>
              )}
            </code>
          </div>
          <Row>
            {endpoint.fields.map(field => (
              <Col md={field.type === "textarea" ? 12 : 6} key={field.key} className="mb-3">
                <Label className="form-label fw-medium">
                  {field.label} {field.required && <span className="text-danger">*</span>}
                </Label>
                {field.type === "textarea" ? (
                  <Input type="textarea" rows={4} placeholder={field.placeholder}
                    value={formData[field.key]} onChange={e => handleChange(field.key, e.target.value)}
                    className="form-control playground-input" />
                ) : field.type === "file" ? (
                  <Input type="file" accept={field.accept || "image/*"} className="form-control playground-input"
                    onChange={e => handleFileChange(field.key, e.target.files[0])} />
                ) : field.type === "select" ? (
                  <Input type="select" value={formData[field.key]}
                    onChange={e => handleChange(field.key, e.target.value)} className="form-select playground-input">
                    {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </Input>
                ) : (
                  <Input type={field.type} placeholder={field.placeholder}
                    value={formData[field.key]} onChange={e => handleChange(field.key, e.target.value)}
                    className="form-control playground-input" />
                )}
              </Col>
            ))}
          </Row>
          <div className="d-flex gap-2">
            <Button color="primary" className="btn-send-request" onClick={handleSubmit} disabled={loading}>
              {loading
                ? <><Spinner size="sm" className="me-2" /> {uploadStatus || "Sending..."}</>
                : <><i className="ri-send-plane-fill me-2"></i>Send Request</>}
            </Button>
            {POSTMAN_AVAILABLE.has(endpoint.id) && (
              <a
                href={`${BASE_URL}/postman/${endpoint.id}.postman_collection.json`}
                download={`${endpoint.id}.postman_collection.json`}
                title="Download Postman collection (presigned-upload flow)"
                className="btn btn-info btn-soft-info d-inline-flex align-items-center"
                style={{ textDecoration: "none" }}>
                <i className="ri-download-2-line me-1"></i>Download Postman
              </a>
            )}
          </div>
        </CardBody>
      </Collapse>
    </Card>
  );
};

// ─── Main Component ───
const ApiPlayground = () => {
  document.title = "API Playground | ApiHub Dashboard";
  const [activeTab, setActiveTab] = useState("1");
  const [apiKeys, setApiKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [keysLoaded, setKeysLoaded] = useState(false);
  const [apiKey, setApiKey] = useState(() => localStorage.getItem("playground_apikey") || "");
  // Create-key form state
  const [newKeyName, setNewKeyName] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [createKeyError, setCreateKeyError] = useState("");
  // Faucet state
  const [faucetOpen, setFaucetOpen] = useState(false);
  const [faucetLoading, setFaucetLoading] = useState(false);
  const [faucetData, setFaucetData] = useState(null);
  const [faucetError, setFaucetError] = useState("");
  const [faucetCopied, setFaucetCopied] = useState(false);
  const [results, setResults] = useState([]);
  const [showKey, setShowKey] = useState(false);
  const [pollingId, setPollingId] = useState(null);
  const [pollingLoading, setPollingLoading] = useState(false);
  const [pollingResult, setPollingResult] = useState(null);
  const [pollingActive, setPollingActive] = useState(false);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const pollTimerRef = useRef(null);
  const pollingIdRef = useRef(null);

  useEffect(() => { localStorage.setItem("playground_apikey", apiKey); }, [apiKey]);

  const loadKeys = useCallback(async () => {
    setKeysLoading(true);
    try {
      const res = await getAPIKey();
      const list = (res && res.data) || [];
      const enabled = list.filter(k => k.is_enabled);
      setApiKeys(enabled);
      if (enabled.length > 0 && !enabled.find(k => k.api_key === apiKey)) {
        setApiKey(enabled[0].api_key);
      }
      setKeysLoaded(true);
    } catch (err) {
      console.log("load keys error", err);
      setKeysLoaded(true);
    } finally {
      setKeysLoading(false);
    }
  }, [apiKey]);

  useEffect(() => { loadKeys(); /* eslint-disable-next-line */ }, []);

  const handleCreateKey = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setCreateKeyError("");
    if (!newKeyName.trim()) {
      setCreateKeyError("Please enter a name for your key.");
      return;
    }
    setCreatingKey(true);
    try {
      const res = await createNewAPIKey(newKeyName.trim());
      if (res && res.success) {
        setNewKeyName("");
        await loadKeys();
      } else {
        setCreateKeyError((res && res.message) || "Failed to create API key.");
      }
    } catch (err) {
      setCreateKeyError(typeof err === "string" ? err : (err && err.message) || "Network error.");
    } finally {
      setCreatingKey(false);
    }
  };

  const openFaucet = async () => {
    setFaucetOpen(true);
    setFaucetError("");
    setFaucetCopied(false);
    if (faucetData) return; // already fetched in this session
    setFaucetLoading(true);
    try {
      const res = await requestFaucetCode();
      if (res && res.success) {
        setFaucetData(res.data);
      } else {
        setFaucetError((res && res.message) || "Could not generate faucet code.");
      }
    } catch (err) {
      setFaucetError(typeof err === "string" ? err : (err && err.message) || "Network error.");
    } finally {
      setFaucetLoading(false);
    }
  };

  const copyFaucetCode = () => {
    if (!faucetData || !faucetData.code) return;
    navigator.clipboard.writeText(`/faucet ${faucetData.code}`).then(() => {
      setFaucetCopied(true);
      setTimeout(() => setFaucetCopied(false), 2000);
    }).catch(() => {});
  };

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setPollingActive(false);
  }, []);

  const pollOnce = useCallback(async (id) => {
    if (!id || !apiKey) return null;
    setPollingLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api-call/request-status/${id}`, { headers: { apikey: apiKey } });
      const data = await res.json();
      setPollingResult(data);
      return data;
    } catch (err) {
      setPollingResult({ error: err.message });
      return null;
    } finally {
      setPollingLoading(false);
    }
  }, [apiKey]);

  const pollStatus = useCallback(async () => {
    if (!pollingId) return;
    await pollOnce(pollingId);
  }, [pollingId, pollOnce]);

  // Auto-poll loop: starts when pollingActive=true, stops on terminal status / max attempts
  useEffect(() => {
    if (!pollingActive || !pollingId) return;
    if (pollingIdRef.current !== pollingId) {
      pollingIdRef.current = pollingId;
      setPollingAttempts(0);
    }

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const data = await pollOnce(pollingId);
      if (cancelled) return;
      setPollingAttempts(a => a + 1);
      const status = data && data.data && data.data.request_status;
      if (status && TERMINAL_STATUSES.includes(status)) {
        setPollingActive(false);
        return;
      }
      if (pollingAttempts + 1 >= POLL_MAX_ATTEMPTS) {
        setPollingActive(false);
        return;
      }
      pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [pollingActive, pollingId]);

  const handleResult = useCallback((result) => {
    setResults(prev => [{ ...result, timestamp: new Date().toLocaleTimeString() }, ...prev].slice(0, 20));
    // Auto-extract request_id and start auto-polling
    if (result.data && result.data.data && result.data.data.request_id) {
      const newId = result.data.data.request_id;
      setPollingId(newId);
      setPollingResult(null);
      setPollingAttempts(0);
      // Kick off immediate poll, then start auto-loop
      pollOnce(newId).then(data => {
        const status = data && data.data && data.data.request_status;
        if (!status || !TERMINAL_STATUSES.includes(status)) {
          setPollingActive(true);
        }
      });
    } else if (result.data && result.data.request_id) {
      // Some endpoints respond { request_id } at top level
      const newId = result.data.request_id;
      setPollingId(newId);
      setPollingResult(null);
      setPollingAttempts(0);
      pollOnce(newId).then(data => {
        const status = data && data.data && data.data.request_status;
        if (!status || !TERMINAL_STATUSES.includes(status)) {
          setPollingActive(true);
        }
      });
    }
  }, [pollOnce]);

  const clearResults = () => {
    stopPolling();
    setResults([]);
    setPollingId(null);
    setPollingResult(null);
    setPollingAttempts(0);
  };

  const currentStatus = pollingResult && pollingResult.data && pollingResult.data.request_status;
  const isTerminal = currentStatus && TERMINAL_STATUSES.includes(currentStatus);
  const statusColor = currentStatus === "COMPLETED" ? "success"
    : currentStatus === "FAILED" || currentStatus === "CANCELLED" ? "danger"
    : currentStatus === "IN_PROCESSING" ? "warning"
    : "info";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="API Playground" pageTitle="Apps" />

          {/* API Key Selector */}
          <Row className="mb-4">
            <Col lg={12}>
              <Card className="apikey-card">
                <CardBody>
                  <Row className="align-items-end">
                    <Col md={8}>
                      <div className="d-flex align-items-center gap-3 mb-2">
                        <div className="apikey-icon-wrap">
                          <i className="ri-key-2-fill"></i>
                        </div>
                        <div>
                          <h5 className="mb-0">API Key</h5>
                          <small className="text-muted">
                            {keysLoading ? "Loading your keys..." :
                              apiKeys.length === 0 ? "Create your first API key to start using the playground"
                                : "Select one of your API keys for requests"}
                          </small>
                        </div>
                      </div>
                      {keysLoaded && apiKeys.length === 0 ? (
                        <Form onSubmit={handleCreateKey} className="mt-3">
                          <div className="d-flex gap-2 flex-wrap">
                            <Input
                              type="text"
                              placeholder="Name your key (e.g. My App)"
                              value={newKeyName}
                              onChange={e => setNewKeyName(e.target.value)}
                              className="apikey-input"
                              style={{ flex: "1 1 240px" }}
                              invalid={!!createKeyError}
                            />
                            <Button color="primary" type="submit" disabled={creatingKey}>
                              {creatingKey ? <><Spinner size="sm" className="me-2" />Creating...</> : <><i className="ri-add-line me-1"></i>Create Key</>}
                            </Button>
                          </div>
                          {createKeyError && <FormFeedback className="d-block">{createKeyError}</FormFeedback>}
                          <small className="text-muted d-block mt-2">
                            <i className="ri-information-line me-1"></i>
                            No credits? Click <strong>Get Free Credits</strong> on the right after creating your key.
                          </small>
                        </Form>
                      ) : (
                        <>
                          <div className="apikey-input-group mt-3 d-flex gap-2">
                            <Input type="select" value={apiKey}
                              onChange={e => setApiKey(e.target.value)}
                              disabled={keysLoading || apiKeys.length === 0}
                              className="form-select apikey-input">
                              {apiKeys.length === 0 && <option value="">— No keys available —</option>}
                              {apiKeys.map(k => {
                                const masked = k.api_key.length > 12
                                  ? `${k.api_key.slice(0, 6)}...${k.api_key.slice(-4)}`
                                  : k.api_key;
                                return (
                                  <option key={k.api_key} value={k.api_key}>
                                    {(k.name || "Unnamed")} — {masked}
                                  </option>
                                );
                              })}
                            </Input>
                            {apiKey && (
                              <Button color="light" className="btn-toggle-key" onClick={() => setShowKey(!showKey)} title={showKey ? "Hide key" : "Show key"}>
                                <i className={showKey ? "ri-eye-off-line" : "ri-eye-line"}></i>
                              </Button>
                            )}
                          </div>
                          {showKey && apiKey && (
                            <div className="mt-2">
                              <code className="text-muted" style={{ wordBreak: "break-all" }}>{apiKey}</code>
                            </div>
                          )}
                        </>
                      )}
                    </Col>
                    <Col md={4} className="text-end mt-3 mt-md-0 d-flex flex-column align-items-md-end gap-2">
                      <Button
                        color="warning"
                        onClick={openFaucet}
                        className="d-inline-flex align-items-center"
                        style={{ background: "linear-gradient(135deg,#fbbf24,#d97706)", border: "none", color: "#fff" }}
                      >
                        <i className="ri-gift-2-fill me-1"></i> Get Free Credits
                      </Button>
                      {apiKey && (
                        <Badge color="success" className="px-3 py-2 fs-12">
                          <i className="ri-check-line me-1"></i> Key Active
                        </Badge>
                      )}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            {/* Left: Endpoints */}
            <Col xl={7} lg={7}>
              <Nav tabs className="nav-tabs-custom playground-tabs mb-3">
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === "1" })} onClick={() => setActiveTab("1")}>
                    <i className="ri-image-line me-1"></i> Image APIs
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === "2" })} onClick={() => setActiveTab("2")}>
                    <i className="ri-movie-line me-1"></i> Video APIs
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === "6" })} onClick={() => setActiveTab("6")}>
                    <i className="ri-clapperboard-line me-1"></i> Video+Audio Gen
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === "3" })} onClick={() => setActiveTab("3")}>
                    <i className="ri-music-2-line me-1"></i> Song APIs
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === "5" })} onClick={() => setActiveTab("5")}>
                    <i className="ri-copper-diamond-line me-1"></i> Identify APIs
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === "4" })} onClick={() => setActiveTab("4")}>
                    <i className="ri-loader-line me-1"></i> Status
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
                  {IMAGE_ENDPOINTS.map(ep => (
                    <EndpointCard key={ep.id} endpoint={ep} apiKey={apiKey} onResult={handleResult} />
                  ))}
                </TabPane>
                <TabPane tabId="2">
                  {VIDEO_ENDPOINTS.map(ep => (
                    <EndpointCard key={ep.id} endpoint={ep} apiKey={apiKey} onResult={handleResult} />
                  ))}
                </TabPane>
                <TabPane tabId="6">
                  {VIDEO_AUDIO_ENDPOINTS.map(ep => (
                    <EndpointCard key={ep.id} endpoint={ep} apiKey={apiKey} onResult={handleResult} />
                  ))}
                </TabPane>
                <TabPane tabId="3">
                  {SONG_ENDPOINTS.map(ep => (
                    <EndpointCard key={ep.id} endpoint={ep} apiKey={apiKey} onResult={handleResult} />
                  ))}
                </TabPane>
                <TabPane tabId="5">
                  {IDENTIFY_ENDPOINTS.map(ep => (
                    <EndpointCard key={ep.id} endpoint={ep} apiKey={apiKey} onResult={handleResult} />
                  ))}
                </TabPane>
                <TabPane tabId="4">
                  <EndpointCard endpoint={REQUEST_STATUS_ENDPOINT} apiKey={apiKey} onResult={handleResult} />
                </TabPane>
              </TabContent>
            </Col>

            {/* Right: Results Panel */}
            <Col xl={5} lg={5}>
              <Card className="results-card sticky-top" style={{ top: "80px" }}>
                <CardHeader className="d-flex align-items-center justify-content-between">
                  <h5 className="mb-0"><i className="ri-terminal-box-line me-2"></i>Response Console</h5>
                  <div className="d-flex gap-2">
                    {pollingId && (
                      <>
                        {pollingActive ? (
                          <Button size="sm" color="warning" outline onClick={stopPolling}>
                            <i className="ri-stop-circle-line me-1"></i>Stop Auto-Poll
                          </Button>
                        ) : !isTerminal && (
                          <Button size="sm" color="success" outline onClick={() => setPollingActive(true)}>
                            <i className="ri-play-circle-line me-1"></i>Resume Auto-Poll
                          </Button>
                        )}
                        <Button size="sm" color="info" outline onClick={pollStatus} disabled={pollingLoading}>
                          {pollingLoading ? <Spinner size="sm" /> : <><i className="ri-refresh-line me-1"></i>Refresh</>}
                        </Button>
                      </>
                    )}
                    <Button size="sm" color="danger" outline onClick={clearResults}>
                      <i className="ri-delete-bin-line me-1"></i>Clear
                    </Button>
                  </div>
                </CardHeader>
                <CardBody className="results-body">
                  {pollingId && (
                    <div className="polling-banner mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <i className="ri-flashlight-line text-warning"></i>
                          <span className="fw-medium">Active Request</span>
                          {currentStatus && (
                            <Badge color={statusColor} className="px-2 py-1">
                              {pollingActive && !isTerminal && <Spinner size="sm" className="me-1" style={{ width: "0.6rem", height: "0.6rem" }} />}
                              {currentStatus}
                            </Badge>
                          )}
                        </div>
                        {pollingActive && !isTerminal && (
                          <small className="text-muted">
                            Auto-polling • {pollingAttempts} {pollingAttempts === 1 ? "check" : "checks"}
                          </small>
                        )}
                      </div>
                      <code className="d-block text-truncate mb-2">{pollingId}</code>
                      {pollingActive && !isTerminal && (
                        <Progress animated value={100} color={statusColor} style={{ height: "4px" }} className="mb-2" />
                      )}
                      {pollingResult && (
                        <pre className="polling-result">{JSON.stringify(pollingResult, null, 2)}</pre>
                      )}
                    </div>
                  )}

                  {results.length === 0 ? (
                    <div className="empty-results text-center py-5">
                      <i className="ri-code-s-slash-line display-4 text-muted"></i>
                      <p className="text-muted mt-3">Send a request to see the response here</p>
                    </div>
                  ) : (
                    results.map((r, i) => (
                      <div key={i} className={`result-item mb-3 ${r.error ? "result-error" : "result-success"}`}>
                        <div className="result-header d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2">
                            <Badge color={r.error ? "danger" : "success"}>{r.status || "ERR"}</Badge>
                            <span className="fw-medium">{r.endpoint}</span>
                          </div>
                          <small className="text-muted">
                            <i className="ri-time-line me-1"></i>{r.time}s • {r.timestamp}
                          </small>
                        </div>
                        <pre className="result-body mt-2">
                          {r.data ? JSON.stringify(r.data, null, 2) : r.message}
                        </pre>
                      </div>
                    ))
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Faucet Modal */}
        <Modal isOpen={faucetOpen} toggle={() => setFaucetOpen(false)} centered size="md">
          <ModalHeader toggle={() => setFaucetOpen(false)}>
            <i className="ri-gift-2-fill text-warning me-2"></i> Free Faucet Credits
          </ModalHeader>
          <ModalBody className="p-4">
            {faucetLoading ? (
              <div className="text-center py-4">
                <Spinner color="warning" />
                <p className="text-muted mt-3 mb-0">Generating your faucet code...</p>
              </div>
            ) : faucetError ? (
              <Alert color="danger" className="mb-0">{faucetError}</Alert>
            ) : faucetData ? (
              <div>
                <p className="fs-15 mb-3">
                  Send the command below in our Telegram group to receive
                  {" "}<strong>{faucetData.credit_amount} free credits</strong>.
                </p>
                <Label className="fw-medium mb-1">Your faucet command</Label>
                <div className="d-flex gap-2 mb-3">
                  <Input readOnly value={`/faucet ${faucetData.code}`} style={{ fontFamily: "monospace", fontWeight: 600 }} />
                  <Button color={faucetCopied ? "success" : "primary"} onClick={copyFaucetCode}>
                    <i className={faucetCopied ? "ri-check-line me-1" : "ri-file-copy-line me-1"}></i>
                    {faucetCopied ? "Copied" : "Copy"}
                  </Button>
                </div>
                <ol className="text-muted fs-13 ps-3 mb-3">
                  <li>Click <strong>Open Telegram Group</strong> below.</li>
                  <li>Paste the command above and send it in the group.</li>
                  <li>Our bot will reply and add credits to your account instantly.</li>
                </ol>
                <Alert color="warning" className="fs-12 mb-3 py-2">
                  <i className="ri-time-line me-1"></i>
                  Code expires in {Math.max(1, Math.round((faucetData.ttl_seconds || 900) / 60))} minutes. One claim per Telegram account.
                </Alert>
                <a
                  href={faucetData.group_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary w-100"
                >
                  <i className="ri-telegram-fill me-1"></i> Open Telegram Group
                </a>
              </div>
            ) : null}
          </ModalBody>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default ApiPlayground;
